/**
 * Inline Search Component
 * Handles search input with dropdown results (non-modal approach)
 * With mobile-specific behavior:
 * - When input is always visible (in header), focusing expands to full-screen overlay
 * - When trigger button is used, clicking it expands to full-screen overlay
 */

class SearchInline extends HTMLElement {
  constructor() {
    super();

    this.refs = {
      triggerButton: this.querySelector('[ref="triggerButton"]'),
      inputWrapper: this.querySelector('[ref="inputWrapper"]'),
      searchInput: this.querySelector('[ref="searchInput"]'),
      clearButton: this.querySelector('[ref="clearButton"]'),
      dropdown: this.querySelector('[ref="dropdown"]'),
      dropdownContent: this.querySelector('[ref="dropdownContent"]'),
      backdrop: this.querySelector('[ref="backdrop"]'),
    };

    // IMPORTANT: This must match your section filename (predictive-search-inline.liquid)
    this.sectionId = this.dataset.sectionId || "predictive-search-inline";
    this.abortController = null;
    this.debounceTimer = null;
    this.isOpen = false;
    this.isMobileExpanded = false;

    this.bindEvents();
  }

  bindEvents() {
    const { triggerButton, searchInput, clearButton, backdrop } = this.refs;

    // Mobile trigger button (if visible)
    triggerButton?.addEventListener(
      "click",
      this.handleTriggerClick.bind(this)
    );

    // Input events
    searchInput?.addEventListener("input", this.handleInput.bind(this));
    searchInput?.addEventListener("focus", this.handleFocus.bind(this));
    searchInput?.addEventListener("keydown", this.handleKeydown.bind(this));

    // Clear button
    clearButton?.addEventListener("click", this.handleClear.bind(this));

    // Backdrop click to close
    backdrop?.addEventListener("click", this.closeMobile.bind(this));

    // Click outside to close (desktop only)
    document.addEventListener("click", this.handleClickOutside.bind(this));

    // Escape key to close
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        if (this.isMobileExpanded) {
          this.closeMobile();
        } else if (this.isOpen) {
          this.close();
        }
      }
    });

    // Handle window resize
    window.addEventListener("resize", this.handleResize.bind(this));
  }

  handleTriggerClick() {
    this.expandMobile();
  }

  /**
   * Check if we're on mobile viewport
   */
  isMobile() {
    return window.innerWidth < 750;
  }

  /**
   * Expand to full-screen mobile search overlay
   */
  expandMobile() {
    if (!this.isMobile()) return;

    this.isMobileExpanded = true;
    this.setAttribute("data-mobile-expanded", "true");

    // Show backdrop
    if (this.refs.backdrop) {
      this.refs.backdrop.hidden = false;
    }

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Focus the input after a short delay for animation
    setTimeout(() => {
      this.refs.searchInput?.focus();
    }, 100);
  }

  /**
   * Close the mobile full-screen overlay
   */
  closeMobile() {
    this.isMobileExpanded = false;
    this.setAttribute("data-mobile-expanded", "false");

    // Hide backdrop
    if (this.refs.backdrop) {
      this.refs.backdrop.hidden = true;
    }

    // Close dropdown too
    this.close();

    // Restore body scroll
    document.body.style.overflow = "";

    // Clear input
    if (this.refs.searchInput) {
      this.refs.searchInput.value = "";
    }
    this.toggleClearButton(false);
  }

  handleResize() {
    // If we're above mobile breakpoint and mobile is expanded, close it
    if (!this.isMobile() && this.isMobileExpanded) {
      this.closeMobile();
    }
  }

  handleInput(event) {
    const query = event.target.value.trim();

    // Show/hide clear button
    this.toggleClearButton(query.length > 0);

    // On mobile, if user starts typing, expand to full screen mode
    if (this.isMobile() && query.length > 0 && !this.isMobileExpanded) {
      this.expandMobile();
    }

    // Debounce the search
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (query.length > 0) {
        this.search(query);
      } else {
        this.close();
      }
    }, 300);
  }

  handleFocus() {
    const query = this.refs.searchInput?.value.trim();

    // On mobile, focusing the input should expand to full-screen mode
    if (this.isMobile() && !this.isMobileExpanded) {
      this.expandMobile();
    }

    // If there's already content and a query, show dropdown
    if (
      query &&
      query.length > 0 &&
      this.refs.dropdownContent?.innerHTML.trim()
    ) {
      this.open();
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      if (this.isMobileExpanded) {
        this.closeMobile();
      } else {
        this.close();
        this.refs.searchInput?.blur();
      }
      return;
    }

    if (event.key === "Enter") {
      // Let form submit naturally or navigate to first result
      return;
    }

    // Arrow key navigation
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      this.navigateResults(event.key === "ArrowDown" ? 1 : -1);
    }
  }

  handleClear() {
    if (this.refs.searchInput) {
      this.refs.searchInput.value = "";
    }
    this.toggleClearButton(false);
    this.close();

    // On mobile, closing should also close the full-screen overlay
    if (this.isMobile() && this.isMobileExpanded) {
      this.closeMobile();
    } else {
      this.refs.searchInput?.focus();
    }
  }

  handleClickOutside(event) {
    // Don't close if clicking inside the component
    if (this.contains(event.target)) return;

    // On desktop, close dropdown when clicking outside
    if (this.isOpen && !this.isMobile()) {
      this.close();
    }
  }

  toggleClearButton(show) {
    if (this.refs.clearButton) {
      this.refs.clearButton.hidden = !show;
    }
  }

  async search(query) {
    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    try {
      // Build the predictive search URL
      const url = new URL(
        window.Theme?.routes?.predictive_search_url || "/search/suggest",
        window.location.origin
      );
      url.searchParams.set("q", query);
      url.searchParams.set("section_id", this.sectionId);
      url.searchParams.set(
        "resources[type]",
        "product,collection,article,page"
      );
      url.searchParams.set("resources[limit]", "10");
      url.searchParams.set("resources[limit_scope]", "each");
      url.searchParams.set("resources[options][fields]", "title,product_type,variants.sku");

      const response = await fetch(url, {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error("Search request failed");
      }

      const html = await response.text();
      this.renderResults(html);
      this.open();
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Search error:", error);
      }
    }
  }

  renderResults(html) {
    // Parse the HTML response and extract the section content
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Look for the section wrapper - Shopify wraps sections in #shopify-section-{section_id}
    const sectionContent = doc.querySelector(
      `#shopify-section-${this.sectionId}`
    );

    if (sectionContent && this.refs.dropdownContent) {
      this.refs.dropdownContent.innerHTML = sectionContent.innerHTML;
    } else {
      // Fallback: try to find any predictive search results
      const fallbackContent =
        doc.querySelector(".search-results-inline") ||
        doc.querySelector(".predictive-search-dropdown") ||
        doc.querySelector("[data-search-results]");

      if (fallbackContent && this.refs.dropdownContent) {
        this.refs.dropdownContent.innerHTML = fallbackContent.outerHTML;
      }
    }
  }

  navigateResults(direction) {
    const links = this.refs.dropdownContent?.querySelectorAll("a");
    if (!links || links.length === 0) return;

    const currentFocus = this.refs.dropdownContent?.querySelector("a:focus");
    let currentIndex = Array.from(links).indexOf(currentFocus);

    if (currentIndex === -1) {
      currentIndex = direction === 1 ? -1 : links.length;
    }

    const nextIndex = currentIndex + direction;

    if (nextIndex >= 0 && nextIndex < links.length) {
      links[nextIndex].focus();
    } else if (nextIndex < 0) {
      this.refs.searchInput?.focus();
    }
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.setAttribute("data-open", "true");

    if (this.refs.dropdown) {
      this.refs.dropdown.hidden = false;
    }

    // Update ARIA
    this.refs.searchInput?.setAttribute("aria-expanded", "true");
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.setAttribute("data-open", "false");

    if (this.refs.dropdown) {
      this.refs.dropdown.hidden = true;
    }

    // Update ARIA
    this.refs.searchInput?.setAttribute("aria-expanded", "false");
  }
}

// Register custom element
if (!customElements.get("search-inline")) {
  customElements.define("search-inline", SearchInline);
}

export { SearchInline };
